import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { newGetNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../../services/userService';
import { useNotification } from '../../../context/NotificationContext';
import moment from 'moment-timezone';

function Notifications() {
  const navigate = useNavigate();
  const { updateUnreadCount } = useNotification();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const observerRef = useRef();
  const lastNotificationRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const response = await newGetNotifications({
        page,
        limit: 20,
        search: debouncedSearchTerm || undefined,
      });

      if (response && response.notifications) {
        const newNotifications = response.notifications || [];
        const pagination = response.pagination || {};

        if (append) {
          setNotifications((prev) => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }
        setHasMore(pagination.hasNextPage ?? false);
        setTotalCount(pagination.totalCount ?? 0);
        setCurrentPage(page);
      } else {
        if (!append) setNotifications([]);
        setHasMore(false);
      }
    } catch (err) {
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
      if (!append) setNotifications([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchNotifications(1, false);
  }, [debouncedSearchTerm]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) fetchNotifications(currentPage + 1, true);
  }, [loadingMore, hasMore, currentPage, fetchNotifications]);

  useEffect(() => {
    if (loading || !hasMore || loadingMore || notifications.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
          fetchNotifications(currentPage + 1, true);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    observerRef.current = observer;
    const last = lastNotificationRef.current;
    if (last) observer.observe(last);
    return () => observer.disconnect();
  }, [loading, hasMore, loadingMore, currentPage, fetchNotifications, notifications.length]);

  const handleNotificationClick = async (notification) => {
    try {
      await markNotificationAsRead({ notificationId: notification.id });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: 1 } : n))
      );
      const newUnreadCount = notifications.filter((n) => n.id !== notification.id && !n.is_read).length;
      updateUnreadCount(newUnreadCount);
      navigate(`/user/notifications/notificationDetails`, { state: { notification } });
    } catch (err) {
      toast.error('Something went wrong');
      navigate(`/user/notifications/notificationDetails`, { state: { notification } });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const updatedNotifications = notifications.map((n) => ({ ...n, is_read: 1 }));
      setNotifications(updatedNotifications);
      updateUnreadCount(0);
      const response = await markAllNotificationsAsRead();
      if (response?.success) {
        toast.success('All notifications marked as read');
      }
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const m = moment.utc(dateStr).local();
    const now = moment();
    if (m.isSame(now, 'day')) return m.format('h:mm A');
    if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
    if (m.isSame(now, 'year')) return m.format('MMM D');
    return m.format('MMM D, YYYY');
  };

  // Loading state
  if (loading && currentPage === 1) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
            <p className="text-gray-500 mt-1">Your updates and alerts</p>
          </div>
          <div className="flex flex-col items-center justify-center py-16">
            <div
              className="w-10 h-10 border-2 border-[#4F6BFF] border-t-transparent rounded-full animate-spin"
              role="status"
              aria-label="Loading"
            />
            <p className="mt-4 text-gray-500 text-sm">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && notifications.length === 0) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
          </div>
          <div className="rounded-2xl border border-red-100 bg-red-50/50 p-8 text-center">
            <Icon icon="fluent:alert-circle-24-regular" className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-700 font-medium">{error}</p>
            <button
              type="button"
              onClick={() => fetchNotifications(1, false)}
              className="mt-4 px-4 py-2 rounded-xl bg-red-100 text-red-700 font-medium hover:bg-red-200 transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#4F6BFF] transition-colors mb-2"
          >
            <Icon icon="mdi:arrow-left" width="18" height="18" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-0.5">
            {totalCount > 0
              ? `${totalCount} notification${totalCount !== 1 ? 's' : ''}`
              : 'Your updates and alerts'}
          </p>
        </div>
        {notifications.some((n) => !n.is_read) && (
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-[#4F6BFF] bg-indigo-50 hover:bg-indigo-100 transition-colors shrink-0"
          >
            <Icon icon="mdi:check-all" width="18" height="18" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Icon
            icon="fluent:search-20-regular"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search notifications..."
            aria-label="Search notifications"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:border-[#4F6BFF] focus:ring-2 focus:ring-[#4F6BFF]/20 outline-none transition-all text-gray-900 placeholder:text-gray-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              aria-label="Clear search"
            >
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex flex-col gap-2">
        {notifications.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-gray-50/50 py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Icon icon="fluent:alert-off-24-regular" className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-1">
              {searchTerm ? 'No matching notifications' : 'No notifications yet'}
            </h3>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              {searchTerm
                ? 'Try a different search term.'
                : 'When you get notifications, they’ll show up here.'}
            </p>
          </div>
        ) : (
          <>
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                ref={index === notifications.length - 1 ? lastNotificationRef : null}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
                className={`
                  group flex gap-4 p-4 rounded-2xl border cursor-pointer transition-all text-left
                  ${!notification.is_read
                    ? 'bg-indigo-50/60 border-indigo-100 hover:bg-indigo-50 hover:border-indigo-200'
                    : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'}
                `}
              >
                <div
                  className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                    !notification.is_read ? 'bg-[#4F6BFF] text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <Icon icon="fluent:alert-20-regular" width="20" height="20" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-1 pr-6">
                      {notification.title}
                    </h3>
                    {!notification.is_read && (
                      <span className="shrink-0 w-2 h-2 rounded-full bg-[#4F6BFF]" aria-hidden />
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mt-0.5 line-clamp-2">{notification.body}</p>
                  <div className="flex items-center gap-1.5 mt-2 text-gray-400 text-xs">
                    <Icon icon="mdi:clock-outline" width="14" height="14" />
                    <span>{formatTime(notification.createdAt)}</span>
                  </div>
                </div>
                <Icon
                  icon="fluent:chevron-right-20-regular"
                  className="shrink-0 w-5 h-5 text-gray-300 group-hover:text-gray-500 mt-1"
                />
              </div>
            ))}

            {loadingMore && (
              <div className="flex justify-center py-6">
                <div
                  className="w-8 h-8 border-2 border-[#4F6BFF] border-t-transparent rounded-full animate-spin"
                  role="status"
                />
              </div>
            )}

            {hasMore && !loadingMore && notifications.length > 0 && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  type="button"
                  onClick={loadMore}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Load more
                </button>
              </div>
            )}

            {!hasMore && notifications.length > 0 && (
              <p className="text-center text-gray-400 text-sm py-4">You’ve seen all notifications</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Notifications;
